import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { listTickets } from '@mkcore/portal/schema';
import { Card, EmptyState, Skeleton, buttonVariants } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { TicketState } from '@/features/portal/tickets';

const search = z.object({ state: z.enum(['open', 'closed']).optional() });

/** The tickets of the client (CO-0661): open and closed, newest change first. */
export const Route = createFileRoute('/portal/_client/tickets/')({
  validateSearch: search,
  component: Tickets,
});

function Tickets() {
  const { t } = useTranslation();
  const { state = 'open' } = Route.useSearch();
  const q = useQuery({ queryKey: ['portal', 'tickets', state], queryFn: () => call(listTickets, { state }) });
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('portal.modules.tickets')}</h1>
        <Link to="/portal/tickets/new" className={buttonVariants()}>
          <Plus /> {t('portal.tickets.new')}
        </Link>
      </div>
      <nav aria-label={t('portal.tickets.filter')} className="flex gap-2 text-sm">
        {(['open', 'closed'] as const).map((s) => (
          <Link key={s} to="/portal/tickets" search={{ state: s }} className={`rounded-md px-3 py-1.5 ${s === state ? 'bg-sunken font-bold' : 'hover:bg-sunken'}`} aria-current={s === state ? 'page' : undefined}>
            {t(`portal.tickets.${s}`)}
          </Link>
        ))}
      </nav>
      {!q.data ? (
        <Skeleton className="h-32" />
      ) : !q.data.items.length ? (
        <EmptyState title={t(state === 'open' ? 'portal.tickets.noneOpen' : 'portal.tickets.noneClosed')} />
      ) : (
        <ul className="grid gap-2">
          {q.data.items.map((x) => (
            <li key={x.id}>
              <Card className="grid gap-1 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link to="/portal/tickets/$id" params={{ id: x.id }} className="font-bold hover:underline">
                    <span className="text-fg-3 tabular">{x.key}</span> {x.subject}
                  </Link>
                  <TicketState status={x.status} />
                </div>
                <p className="text-xs text-fg-2">
                  {[x.requesterName, x.projectName, t('portal.tickets.updated', { at: formatDateTime(x.updatedAt) })].filter(Boolean).join(' · ')}
                </p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
